import React, { useState, useEffect } from 'react'
import {
  Typography,
  Button,
  Card,
  Col,
  Row,
  Space,
  Modal,
  Input,
  Checkbox,
  Select,
  Tag,
  Alert,
  Spin,
  Divider,
  List,
} from 'antd'
import {
  ExportOutlined as OpenIcon,
  CopyOutlined as CopyIcon,
  CheckCircleOutlined as CompleteIcon,
  CheckCircleOutlined,
  EditOutlined,
  CloseCircleOutlined,
  CloseCircleOutlined as ReleaseIcon,
  ClockCircleOutlined as TimerIcon,
  BankOutlined as CompanyIcon,
  TrophyOutlined,
  DollarOutlined,
  TeamOutlined,
  GlobalOutlined,
  RocketOutlined,
  StarOutlined,
  ThunderboltOutlined,
  CrownOutlined,
  FireOutlined,
  SafetyOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { fetchCurrentLead, fetchLeadById, completeLead, releaseLead, getNextLead } from '../store/slices/leadSlice'
import { fetchPersonalStats } from '../store/slices/statsSlice'
import { apiService } from '../services/apiService'
import { PlusOutlined } from '@ant-design/icons'
import { message } from 'antd'

function getIstYearAndMonth(): { year: string; month: string } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: 'short',
  }).formatToParts(new Date())

  const year = parts.find((part) => part.type === 'year')?.value || new Date().getFullYear().toString()
  const month = parts.find((part) => part.type === 'month')?.value || 'Jan'

  return { year, month }
}

function buildDefaultSprintNumber(companyName: string): string {
  const { year, month } = getIstYearAndMonth()
  const usernameSegment = companyName
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

  return `${year}-${month}-${usernameSegment || 'lead'}`
}

function normalizeCcEmailInput(rawValue: string): string {
  return rawValue
    .split(/[\s,;]+/)
    .map((value) => value.trim())
    .filter(Boolean)
    .join(', ')
}

function LeadProcessing() {
  const navigate = useNavigate()
  const { leadId } = useParams<{ leadId: string }>()
  const dispatch = useAppDispatch()
  const { currentLead, loading, nextLeadAvailable } = useAppSelector((state) => state.leads)
  
  // Debug logging for state changes
  console.log('🔍 LeadProcessing render - currentLead:', !!currentLead, 'loading:', loading)
  console.log('🔍 LeadProcessing qualification details:', currentLead?.qualification_details_parsed)
  
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false)
  const [submitAction, setSubmitAction] = useState<'successful' | 'unsuccessful' | 'release'>('unsuccessful')
  const [completionData, setCompletionData] = useState({
    status: 'unsuccessful',
    good_emails_count: 0,
    generic_emails_count: 0,
    phone_numbers_count: 0,
    generic_phones_count: 0,
    primary_source: '',
    is_verified: false,
    not_qualified_reason: '',
    unsuccessful_reason: 'cannot_find_contacts',
    unsuccessful_description: '',
  })
  const [releaseData, setReleaseData] = useState({
    reason: 'cannot_find_contacts',
    description: '',
  })
  const [crmModalOpen, setCrmModalOpen] = useState(false)
  const [crmEmail, setCrmEmail] = useState('')
  const [crmCreating, setCrmCreating] = useState(false)
  const [crmUpdateModalOpen, setCrmUpdateModalOpen] = useState(false)
  const [crmUpdating, setCrmUpdating] = useState(false)
  const [crmUpdateData, setCrmUpdateData] = useState({
    cc_email: '',
    sprint_number: '',
  })

  useEffect(() => {
    if (leadId) {
      // If leadId is provided in URL, fetch specific lead
      dispatch(fetchLeadById(parseInt(leadId)))
    } else {
      // Otherwise fetch current assigned lead
      dispatch(fetchCurrentLead())
    }
  }, [dispatch, leadId])

  // Note: We removed the auto-redirect useEffect because we want users to see 
  // the "Great Job!" page after completing a lead, not be auto-redirected to dashboard

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const handleOpenLink = (url: string) => {
    window.open(url, '_blank')
  }

  const crmLink = currentLead?.crm_link || currentLead?.qualification_details_parsed?.crm_status?.crm_link || null

  const handleOpenCrmUpdateModal = () => {
    if (!currentLead) return

    setCrmUpdateData({
      cc_email: '',
      sprint_number: buildDefaultSprintNumber(currentLead.company_name),
    })
    setCrmUpdateModalOpen(true)
  }

  const handleCloseCrmUpdateModal = () => {
    setCrmUpdateModalOpen(false)
    setCrmUpdateData({
      cc_email: '',
      sprint_number: '',
    })
  }

  const handleCreateCrmProfile = async () => {
    if (!currentLead || !crmEmail.trim()) return
    setCrmCreating(true)
    try {
      const result = await apiService.createCrmProfile(currentLead.id, crmEmail.trim())
      message.success('CRM profile created successfully')
      setCrmModalOpen(false)
      setCrmEmail('')
      // Refresh the current lead to get the new crm_link
      if (leadId) {
        dispatch(fetchLeadById(parseInt(leadId)))
      } else {
        dispatch(fetchCurrentLead())
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || error.response?.data?.details?.detail || 'Failed to create CRM profile'
      message.error(errorMsg)
    } finally {
      setCrmCreating(false)
    }
  }

  const handleUpdateCrm = async () => {
    if (!currentLead) return

    const normalizedCcEmail = normalizeCcEmailInput(crmUpdateData.cc_email)
    if (!normalizedCcEmail) {
      message.error('Add at least one CC email before pushing to CRM')
      return
    }

    if (!crmUpdateData.sprint_number.trim()) {
      message.error('Sprint number is required')
      return
    }

    setCrmUpdating(true)
    try {
      const result = await apiService.updateCrmLead(currentLead.id, {
        cc_email: normalizedCcEmail,
        sprint_number: crmUpdateData.sprint_number.trim(),
      })

      const appointmentText = new Date(result.appointment).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })

      message.success(`CRM updated. Appointment set for ${appointmentText} IST.`)
      handleCloseCrmUpdateModal()
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || error.response?.data?.details?.detail || 'Failed to update CRM'
      message.error(errorMsg)
    } finally {
      setCrmUpdating(false)
    }
  }

  const handleSubmitLead = async () => {
    if (!currentLead) return
    
    setSubmitDialogOpen(false)
    
    try {
      let result: any
      
      if (submitAction === 'release') {
        console.log('🔄 Starting lead release for lead:', currentLead.id)
        console.log('📋 Release data:', releaseData)
        
        result = await dispatch(releaseLead({
          leadId: currentLead.id,
          reason: releaseData.reason,
          description: releaseData.description
        }))
        
        if (releaseLead.fulfilled.match(result)) {
          Modal.success({
            title: 'Lead Released Successfully',
            content: `${currentLead.company_name} has been returned to the queue for other users.`,
            onOk: () => navigate('/dashboard')
          })
        } else {
          throw new Error(result.error?.message || 'Failed to release lead')
        }
      } else {
        // Handle completion (successful or unsuccessful)
        console.log('🚀 Starting lead completion for lead:', currentLead.id)
        console.log('📋 Completion data:', { ...completionData, status: submitAction })
        
        const submissionData = {
          ...completionData,
          status: submitAction
        }
        
        // Map unsuccessful_reason to not_qualified_reason for unsuccessful leads
        if (submitAction === 'unsuccessful' && completionData.unsuccessful_reason) {
          const reasonMapping: { [key: string]: string } = {
            'cannot_find_contacts': 'Cannot find contacts',
            'lead_not_qualified': 'Lead doesn\'t satisfy criterion',
            'technical_issues': 'Technical issues',
            'others': 'Others'
          }
          submissionData.not_qualified_reason = reasonMapping[completionData.unsuccessful_reason] || completionData.unsuccessful_reason
        }
        
        result = await dispatch(completeLead({
          leadId: currentLead.id,
          data: submissionData,
        }))
        
        if (completeLead.fulfilled.match(result)) {
          console.log('✅ Lead completed successfully!')
          dispatch(fetchPersonalStats('today'))
          
          Modal.success({
            title: 'Lead Completed Successfully',
            content: `${currentLead.company_name} has been marked as ${submitAction}.`,
          })
        } else {
          throw new Error(result.error?.message || 'Failed to complete lead')
        }
      }
    } catch (error: any) {
      console.error('❌ Error during lead submission:', error)
      
      Modal.error({
        title: `Failed to ${submitAction === 'release' ? 'Release' : 'Complete'} Lead`,
        content: (
          <div>
            <p>Could not {submitAction === 'release' ? 'release' : 'complete'} the lead "{currentLead.company_name}".</p>
            <p><strong>Error:</strong> {error.message || 'Unknown error occurred'}</p>
            <p>Please try again or contact support if the problem persists.</p>
          </div>
        ),
      })
    }
  }


  const formatTimeRemaining = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${hours}h ${minutes}m`
  }

  const getQualificationIcon = (reason: string) => {
    const lowerReason = reason.toLowerCase()
    
    if (lowerReason.includes('revenue') || lowerReason.includes('turnover') || lowerReason.includes('financial')) {
      return <DollarOutlined style={{ color: '#52c41a' }} />
    }
    if (lowerReason.includes('win') || lowerReason.includes('success') || lowerReason.includes('track record')) {
      return <TrophyOutlined style={{ color: '#52c41a' }} />
    }
    if (lowerReason.includes('experience') || lowerReason.includes('active since') || lowerReason.includes('established')) {
      return <StarOutlined style={{ color: '#52c41a' }} />
    }
    if (lowerReason.includes('diversity') || lowerReason.includes('industries') || lowerReason.includes('sectors')) {
      return <GlobalOutlined style={{ color: '#52c41a' }} />
    }
    if (lowerReason.includes('activity') || lowerReason.includes('bids') || lowerReason.includes('active')) {
      return <RocketOutlined style={{ color: '#52c41a' }} />
    }
    if (lowerReason.includes('size') || lowerReason.includes('large') || lowerReason.includes('employees')) {
      return <TeamOutlined style={{ color: '#52c41a' }} />
    }
    if (lowerReason.includes('platform') || lowerReason.includes('gem') || lowerReason.includes('crm')) {
      return <ThunderboltOutlined style={{ color: '#52c41a' }} />
    }
    if (lowerReason.includes('premium') || lowerReason.includes('quality') || lowerReason.includes('tier')) {
      return <CrownOutlined style={{ color: '#52c41a' }} />
    }
    if (lowerReason.includes('growth') || lowerReason.includes('trending') || lowerReason.includes('rising')) {
      return <FireOutlined style={{ color: '#52c41a' }} />
    }
    if (lowerReason.includes('competitive') || lowerReason.includes('advantage') || lowerReason.includes('unique')) {
      return <SafetyOutlined style={{ color: '#52c41a' }} />
    }
    
    // Default icon
    return <CheckCircleOutlined style={{ color: '#52c41a' }} />
  }

  if (loading) {
    return <Spin size="large" style={{ display: 'flex', justifyContent: 'center', marginTop: 50 }} />
  }

  const handleGetNextLead = async () => {
    const result = await dispatch(getNextLead())
    if (getNextLead.rejected.match(result)) {
      // No leads available, show message and go to dashboard
      Modal.info({
        title: 'No More Leads',
        content: 'There are no more leads available at this time.',
        onOk: () => navigate('/dashboard'),
      })
    }
    // If successful, getNextLead automatically updates currentLead in Redux store
    // Component will re-render and show the new lead
  }

  if (!currentLead) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '60vh',
        textAlign: 'center',
        padding: '40px 20px'
      }}>
        <CheckCircleOutlined 
          style={{ 
            fontSize: '80px', 
            color: '#52c41a', 
            marginBottom: '24px' 
          }} 
        />
        
        <Typography.Title level={2} style={{ marginBottom: '16px' }}>
          Great Job!
        </Typography.Title>
        
        <Typography.Paragraph style={{ 
          fontSize: '16px', 
          color: '#666', 
          marginBottom: '32px',
          maxWidth: '400px'
        }}>
          {loading ? 'Processing your request...' : 'Lead completed successfully! Ready to work on the next one?'}
        </Typography.Paragraph>
        
        <Space size="large">
          <Button 
            type="primary" 
            size="large"
            onClick={handleGetNextLead} 
            loading={loading}
            style={{ 
              minWidth: '160px', 
              height: '48px',
              backgroundColor: '#1677ff',
              borderColor: '#1677ff',
              color: 'white',
              fontWeight: '500'
            }}
          >
            Get Next Lead
          </Button>
          <Button 
            size="large"
            onClick={() => navigate('/dashboard')}
            style={{ 
              minWidth: '160px', 
              height: '48px',
              backgroundColor: '#f6f6f6',
              borderColor: '#d9d9d9',
              color: '#595959',
              fontWeight: '500'
            }}
          >
            Go to Dashboard
          </Button>
        </Space>
      </div>
    )
  }

  return (
    <div>
      <Typography.Title level={2} style={{ marginBottom: 24 }}>
        Processing Lead
      </Typography.Title>

      <Row gutter={24}>
        <Col xs={24} md={16}>
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CompanyIcon style={{ fontSize: 30 }} />
                <Typography.Title level={3} style={{ margin: 0 }}>
                  {currentLead.company_name}
                </Typography.Title>
                <Button
                  icon={<CopyIcon />}
                  size="small"
                  onClick={() => handleCopyToClipboard(currentLead.company_name)}
                  style={{ 
                    marginLeft: 8,
                    backgroundColor: '#1677ff',
                    borderColor: '#1677ff',
                    color: 'white'
                  }}
                />
              </div>

              <Row gutter={16}>
                <Col span={8}>
                  <div>
                    <Typography.Text type="secondary">
                      Lead Score
                    </Typography.Text>
                    <Typography.Title level={4} style={{ margin: '4px 0 0 0' }}>
                      {currentLead.lead_score}
                    </Typography.Title>
                  </div>
                </Col>
                <Col span={8}>
                  <div>
                    <Typography.Text type="secondary">
                      Source
                    </Typography.Text>
                    <div style={{ marginTop: 4 }}>
                      <Tag
                        color={currentLead.is_crm_sourced ? 'blue' : 'default'}
                      >
                        {currentLead.source_type}
                      </Tag>
                    </div>
                  </div>
                </Col>
                <Col span={8}>
                  <div>
                    <Typography.Text type="secondary">
                      Company Key
                    </Typography.Text>
                    <div style={{ marginTop: 4 }}>
                      <Typography.Text code style={{ fontSize: '12px' }}>
                        {currentLead.crm_company_key}
                      </Typography.Text>
                      <Button
                        icon={<CopyIcon />}
                        size="small"
                        onClick={() => handleCopyToClipboard(currentLead.crm_company_key || '')}
                        style={{ 
                          marginLeft: 4,
                          backgroundColor: '#e6f7ff',
                          borderColor: '#91d5ff',
                          color: '#1890ff'
                        }}
                      />
                      <Button
                        icon={<OpenIcon />}
                        size="small"
                        onClick={() => handleOpenLink(`https://app.palladium.primenumbers.in/company/detail/${currentLead.crm_company_key}`)}
                        style={{ 
                          marginLeft: 4,
                          backgroundColor: '#f6ffed',
                          borderColor: '#b7eb8f',
                          color: '#52c41a'
                        }}
                      />
                    </div>
                  </div>
                </Col>
              </Row>

              <div>
                <Typography.Title level={4} style={{ marginBottom: 16 }}>
                  Quick Links
                </Typography.Title>
                <Space wrap>
                  {currentLead.palladium_link && (
                    <Button
                      icon={<OpenIcon />}
                      onClick={() => handleOpenLink(currentLead.palladium_link!)}
                      style={{
                        backgroundColor: '#52c41a',
                        borderColor: '#52c41a',
                        color: 'white'
                      }}
                    >
                      Open Palladium
                    </Button>
                  )}
                  {crmLink && (
                    <Button
                      icon={<OpenIcon />}
                      onClick={() => handleOpenLink(crmLink)}
                      style={{
                        backgroundColor: '#faad14',
                        borderColor: '#faad14',
                        color: 'white'
                      }}
                    >
                      Open CRM
                    </Button>
                  )}
                  {crmLink && (
                    <Button
                      icon={<EditOutlined />}
                      onClick={handleOpenCrmUpdateModal}
                      style={{
                        backgroundColor: '#1677ff',
                        borderColor: '#1677ff',
                        color: 'white',
                      }}
                    >
                      Update CRM
                    </Button>
                  )}
                  {/* Create CRM Profile button when no CRM link exists */}
                  {!crmLink && (
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() => setCrmModalOpen(true)}
                      style={{
                        backgroundColor: '#faad14',
                        borderColor: '#faad14',
                        color: 'white'
                      }}
                    >
                      Create CRM Profile
                    </Button>
                  )}
                </Space>
              </div>

              {/* Qualification Criteria Section */}
              {currentLead.qualification_details_parsed && (
                <div>
                  <Typography.Title level={4} style={{ marginBottom: 16 }}>
                    Why This Lead Was Selected
                  </Typography.Title>
                  
                  {/* Qualification Reasons (New Format with Reasons Array) */}
                  {currentLead.qualification_details_parsed.qualification_reasons && (
                    <List
                      header={<Typography.Text strong>Qualification Criteria Met</Typography.Text>}
                      dataSource={currentLead.qualification_details_parsed.qualification_reasons}
                      renderItem={(reason: string, index: number) => (
                        <List.Item>
                          <List.Item.Meta
                            avatar={getQualificationIcon(reason)}
                            title={reason}
                          />
                        </List.Item>
                      )}
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  {/* Qualification Filters (Legacy Format) */}
                  {!currentLead.qualification_details_parsed.qualification_reasons && currentLead.qualification_details_parsed.qualification_filters && (
                    <List
                      header={<Typography.Text strong>Qualification Criteria Met</Typography.Text>}
                      dataSource={Object.entries(currentLead.qualification_details_parsed.qualification_filters)}
                      renderItem={([key, value]: [string, string]) => {
                        const isPassed = value.includes('✅');
                        const cleanDescription = value.replace(/✅|❌/g, '').trim();
                        const formatKey = (str: string) => {
                          return str.replace(/_/g, ' ')
                            .replace(/\b\w/g, l => l.toUpperCase())
                            .replace(/Gem/g, 'GEM')
                            .replace(/Crm/g, 'CRM');
                        };
                        const displayText = `${formatKey(key)}: ${cleanDescription}`;
                        
                        return (
                          <List.Item>
                            <List.Item.Meta
                              avatar={getQualificationIcon(displayText)}
                              title={<Typography.Text strong>{formatKey(key)}</Typography.Text>}
                              description={cleanDescription}
                            />
                          </List.Item>
                        );
                      }}
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  {/* Legacy Parameters (Old Format) */}
                  {!currentLead.qualification_details_parsed.qualification_reasons && currentLead.qualification_details_parsed.parameters_checked && (
                    <List
                      header={<Typography.Text strong>Qualification Criteria Met</Typography.Text>}
                      dataSource={Object.entries(currentLead.qualification_details_parsed.parameters_checked)}
                      renderItem={([key, param]: [string, any]) => {
                        const displayText = `${key.replace('_', ' ')}: ${param.description}`;
                        
                        return (
                          <List.Item>
                            <List.Item.Meta
                              avatar={getQualificationIcon(displayText)}
                              title={<Typography.Text strong style={{ textTransform: 'capitalize' }}>
                                {key.replace('_', ' ')}
                              </Typography.Text>}
                              description={param.description}
                            />
                          </List.Item>
                        );
                      }}
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  {/* Company Profile */}
                  {currentLead.qualification_details_parsed.company_profile && (
                    <div>
                      <Typography.Text strong style={{ color: '#389e0d' }}>
                        Company Profile:
                      </Typography.Text>
                      <div style={{ marginTop: 8 }}>
                        <Typography.Text>
                          <strong>Industry:</strong> {currentLead.qualification_details_parsed.company_profile.industry}
                        </Typography.Text>
                        <br />
                        <Typography.Text>
                          <strong>Track Record:</strong> {currentLead.qualification_details_parsed.company_profile.total_wins}/{currentLead.qualification_details_parsed.company_profile.total_bids} wins 
                          ({currentLead.qualification_details_parsed.company_profile.win_rate} success rate)
                        </Typography.Text>
                        <br />
                        <Typography.Text>
                          <strong>Experience:</strong> Active since {new Date(currentLead.qualification_details_parsed.company_profile.bidding_since).getFullYear()}
                        </Typography.Text>
                        <br />
                        <Typography.Text>
                          <strong>Market Reach:</strong> {currentLead.qualification_details_parsed.company_profile.organizations_worked_with} organizations, competing with {currentLead.qualification_details_parsed.company_profile.competitor_count} others
                        </Typography.Text>
                      </div>
                    </div>
                  )}

                  {/* CRM Status (New Format) */}
                  {currentLead.qualification_details_parsed.crm_status && (
                    <div>
                      <Typography.Text strong style={{ color: '#389e0d' }}>
                        CRM Integration Status:
                      </Typography.Text>
                      <div style={{ marginTop: 8 }}>
                        {currentLead.qualification_details_parsed.crm_status.found ? (
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                              <Typography.Text>
                                Found in CRM (ID: {currentLead.qualification_details_parsed.crm_status.crm_id})
                              </Typography.Text>
                              {currentLead.qualification_details_parsed.crm_status.crm_link && (
                                <Button
                                  icon={<OpenIcon />}
                                  size="small"
                                  onClick={() => handleOpenLink(currentLead.qualification_details_parsed!.crm_status!.crm_link!)}
                                  style={{
                                    marginLeft: 8,
                                    backgroundColor: '#faad14',
                                    borderColor: '#faad14',
                                    color: 'white'
                                  }}
                                >
                                  View CRM
                                </Button>
                              )}
                            </div>
                            <div style={{ marginLeft: 24 }}>
                              {currentLead.qualification_details_parsed.crm_status.paying_customer ? (
                                <Tag color="red">Paying Customer</Tag>
                              ) : (
                                <Tag color="green">Not Paying Customer</Tag>
                              )}
                              {currentLead.qualification_details_parsed.crm_status.assigned_to && (
                                <Tag color="blue" style={{ marginLeft: 8 }}>
                                  Assigned to: {currentLead.qualification_details_parsed.crm_status.assigned_to}
                                </Tag>
                              )}
                              {currentLead.qualification_details_parsed.crm_status.email && (
                                <div style={{ marginTop: 4 }}>
                                  <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                                    CRM Email: {currentLead.qualification_details_parsed.crm_status.email}
                                  </Typography.Text>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <CloseCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                            <Typography.Text>
                              {currentLead.qualification_details_parsed.crm_status.reason || 'Not found in CRM'}
                            </Typography.Text>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Legacy CRM Match Status */}
                  {currentLead.qualification_details_parsed.crm_match && (
                    <div>
                      <Typography.Text strong style={{ color: '#389e0d' }}>
                        CRM Integration Status:
                      </Typography.Text>
                      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center' }}>
                        {currentLead.qualification_details_parsed.crm_match.found ? (
                          <>
                            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                            <Typography.Text>
                              {currentLead.qualification_details_parsed.crm_match.description}
                              {currentLead.qualification_details_parsed.crm_match.crm_company_key && (
                                <Typography.Text code style={{ marginLeft: 8 }}>
                                  Key: {currentLead.qualification_details_parsed.crm_match.crm_company_key}
                                </Typography.Text>
                              )}
                            </Typography.Text>
                          </>
                        ) : (
                          <>
                            <CloseCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                            <Typography.Text>
                              {currentLead.qualification_details_parsed.crm_match.description}
                            </Typography.Text>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Qualification Score */}
                  {currentLead.qualification_details_parsed.qualification_score && (
                    <div>
                      <Typography.Text strong style={{ color: '#389e0d' }}>
                        Qualification Score: {currentLead.qualification_details_parsed.qualification_score.total_score}/100
                      </Typography.Text>
                      <div style={{ marginTop: 8, display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        <Tag color="blue">Revenue: {currentLead.qualification_details_parsed.qualification_score.revenue_score}/20</Tag>
                        <Tag color="green">Activity: {currentLead.qualification_details_parsed.qualification_score.activity_score}/20</Tag>
                        <Tag color="orange">Diversity: {currentLead.qualification_details_parsed.qualification_score.diversity_score}/15</Tag>
                        <Tag color="purple">Win Rate: {currentLead.qualification_details_parsed.qualification_score.win_rate_score}/20</Tag>
                        <Tag color="cyan">Platform: {currentLead.qualification_details_parsed.qualification_score.platform_score}/20</Tag>
                      </div>
                    </div>
                  )}
                </div>
              )}

            </Space>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <TimerIcon />
                <Typography.Title level={4} style={{ margin: 0 }}>Timer</Typography.Title>
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <Typography.Title level={1} style={{ color: '#faad14', margin: 0 }}>
                  {formatTimeRemaining(currentLead.time_remaining_seconds || 0)}
                </Typography.Title>
                <Typography.Text type="secondary">
                  Time Remaining
                </Typography.Text>
              </div>

              <div>
                <Typography.Text type="secondary">
                  Time Elapsed
                </Typography.Text>
                <Typography.Title level={4} style={{ margin: '4px 0 0 0' }}>
                  {Math.floor((currentLead.time_elapsed_seconds || 0) / 60)} minutes
                </Typography.Title>
              </div>

              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  type="primary"
                  icon={<CompleteIcon />}
                  onClick={() => setSubmitDialogOpen(true)}
                  block
                  style={{ 
                    backgroundColor: '#1677ff', 
                    borderColor: '#1677ff',
                    color: 'white',
                    fontWeight: '500',
                    height: '64px',
                    fontSize: '16px'
                  }}
                >
                  Submit Lead
                </Button>
              </Space>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Submit Lead Dialog */}
      <Modal
        title="Submit Lead"
        open={submitDialogOpen}
        onCancel={() => setSubmitDialogOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setSubmitDialogOpen(false)}>
            Cancel
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={handleSubmitLead}
            disabled={
              (submitAction === 'unsuccessful' && completionData.unsuccessful_description.trim().length < 10) ||
              (submitAction === 'successful' && completionData.good_emails_count === 0) ||
              (submitAction === 'release' && releaseData.description.trim().length < 10)
            }
            style={{
              backgroundColor: submitAction === 'release' ? '#faad14' : 
                              submitAction === 'successful' ? '#52c41a' : '#ff4d4f',
              borderColor: submitAction === 'release' ? '#faad14' : 
                          submitAction === 'successful' ? '#52c41a' : '#ff4d4f',
              color: 'white'
            }}
          >
            {submitAction === 'successful' ? 'Complete as Successful' : 
             submitAction === 'unsuccessful' ? 'Complete as Unsuccessful' : 
             'Release Lead'}
          </Button>,
        ]}
        width={700}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Typography.Text strong style={{ fontSize: '16px' }}>What would you like to do with this lead?</Typography.Text>
            <div style={{ marginTop: 12, display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Button
                type={submitAction === 'successful' ? 'primary' : 'default'}
                icon={<CheckCircleOutlined />}
                onClick={() => {
                  setSubmitAction('successful')
                  if (completionData.good_emails_count === 0) {
                    setCompletionData({ ...completionData, good_emails_count: 1 })
                  }
                }}
                style={{
                  height: '48px',
                  flex: 1,
                  minWidth: '180px',
                  backgroundColor: submitAction === 'successful' ? '#52c41a' : undefined,
                  borderColor: submitAction === 'successful' ? '#52c41a' : '#d9d9d9',
                  color: submitAction === 'successful' ? 'white' : undefined
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <div>Successful</div>
                  <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '4px' }}>Found contacts</div>
                </div>
              </Button>
              <Button
                type={submitAction === 'unsuccessful' ? 'primary' : 'default'}
                icon={<CloseCircleOutlined />}
                onClick={() => setSubmitAction('unsuccessful')}
                style={{
                  height: '48px',
                  flex: 1,
                  minWidth: '180px',
                  backgroundColor: submitAction === 'unsuccessful' ? '#ff4d4f' : undefined,
                  borderColor: submitAction === 'unsuccessful' ? '#ff4d4f' : '#d9d9d9',
                  color: submitAction === 'unsuccessful' ? 'white' : undefined
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <div>Unsuccessful</div>
                  <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '4px' }}>No contacts found</div>
                </div>
              </Button>
              <Button
                type={submitAction === 'release' ? 'primary' : 'default'}
                icon={<ReleaseIcon />}
                onClick={() => setSubmitAction('release')}
                style={{
                  height: '48px',
                  flex: 1,
                  minWidth: '180px',
                  backgroundColor: submitAction === 'release' ? '#faad14' : undefined,
                  borderColor: submitAction === 'release' ? '#faad14' : '#d9d9d9',
                  color: submitAction === 'release' ? 'white' : undefined
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <div>Release</div>
                  <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '4px' }}>Return to queue</div>
                </div>
              </Button>
            </div>
          </div>
          
          <Divider />

          {submitAction === 'successful' && (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <div>
                    <Typography.Text strong>Good Emails Found</Typography.Text>
                    <Input
                      type="number"
                      value={completionData.good_emails_count}
                      onChange={(e) =>
                        setCompletionData({
                          ...completionData,
                          good_emails_count: parseInt(e.target.value) || 0,
                        })
                      }
                      style={{ marginTop: 8 }}
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <div>
                    <Typography.Text strong>Generic Emails Found</Typography.Text>
                    <Input
                      type="number"
                      value={completionData.generic_emails_count}
                      onChange={(e) =>
                        setCompletionData({
                          ...completionData,
                          generic_emails_count: parseInt(e.target.value) || 0,
                        })
                      }
                      style={{ marginTop: 8 }}
                    />
                  </div>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <div>
                    <Typography.Text strong>Phone Numbers Found</Typography.Text>
                    <Input
                      type="number"
                      value={completionData.phone_numbers_count}
                      onChange={(e) =>
                        setCompletionData({
                          ...completionData,
                          phone_numbers_count: parseInt(e.target.value) || 0,
                        })
                      }
                      style={{ marginTop: 8 }}
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <div>
                    <Typography.Text strong>Generic Phones Found</Typography.Text>
                    <Input
                      type="number"
                      value={completionData.generic_phones_count}
                      onChange={(e) =>
                        setCompletionData({
                          ...completionData,
                          generic_phones_count: parseInt(e.target.value) || 0,
                        })
                      }
                      style={{ marginTop: 8 }}
                    />
                  </div>
                </Col>
              </Row>
              <div>
                <Typography.Text strong>Primary Source</Typography.Text>
                <Input
                  value={completionData.primary_source}
                  onChange={(e) =>
                    setCompletionData({
                      ...completionData,
                      primary_source: e.target.value,
                    })
                  }
                  placeholder="LinkedIn, Company Website, etc."
                  style={{ marginTop: 8 }}
                />
              </div>
              <div>
                <Checkbox
                  checked={completionData.is_verified}
                  onChange={(e) =>
                    setCompletionData({
                      ...completionData,
                      is_verified: e.target.checked,
                    })
                  }
                >
                  Contacts verified
                </Checkbox>
              </div>
            </>
          )}

          {submitAction === 'unsuccessful' && (
            <>
              <div>
                <Typography.Text strong>Reason</Typography.Text>
                <Select
                  value={completionData.unsuccessful_reason}
                  onChange={(value) =>
                    setCompletionData({
                      ...completionData,
                      unsuccessful_reason: value,
                    })
                  }
                  style={{ width: '100%', marginTop: 8 }}
                >
                  <Select.Option value="cannot_find_contacts">Cannot find contacts</Select.Option>
                  <Select.Option value="lead_not_qualified">Lead doesn't satisfy criterion</Select.Option>
                  <Select.Option value="technical_issues">Technical issues</Select.Option>
                  <Select.Option value="others">Others</Select.Option>
                </Select>
              </div>
              <div>
                <Typography.Text strong>Description</Typography.Text>
                <Input.TextArea
                  rows={3}
                  value={completionData.unsuccessful_description}
                  onChange={(e) =>
                    setCompletionData({
                      ...completionData,
                      unsuccessful_description: e.target.value,
                    })
                  }
                  placeholder="Please describe why this lead was unsuccessful (minimum 10 characters)"
                  style={{ marginTop: 8 }}
                />
              </div>
            </>
          )}

          {submitAction === 'release' && (
            <>
              <Alert
                message="Why are you releasing this lead?"
                description="This will return the lead to the queue for other team members."
                type="warning"
                showIcon
              />
              
              <div>
                <Typography.Text strong>Reason for Release</Typography.Text>
                <Select
                  value={releaseData.reason}
                  onChange={(value) =>
                    setReleaseData({
                      ...releaseData,
                      reason: value,
                    })
                  }
                  style={{ width: '100%', marginTop: 8 }}
                >
                  <Select.Option value="cannot_find_contacts">Cannot Find Contacts</Select.Option>
                  <Select.Option value="not_qualified">Lead Doesn't Satisfy Criterion</Select.Option>
                  <Select.Option value="technical_issues">Technical Issues</Select.Option>
                  <Select.Option value="other">Other</Select.Option>
                </Select>
              </div>

              <div>
                <Typography.Text strong>
                  Description 
                  <Typography.Text 
                    type={releaseData.description.trim().length >= 10 ? "success" : "danger"}
                    style={{ fontSize: '12px', marginLeft: 8 }}
                  >
                    ({releaseData.description.trim().length}/10 minimum)
                  </Typography.Text>
                </Typography.Text>
                <Input.TextArea
                  rows={3}
                  value={releaseData.description}
                  onChange={(e) =>
                    setReleaseData({
                      ...releaseData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Please provide additional details about why you're releasing this lead (minimum 10 characters)..."
                  style={{ 
                    marginTop: 8,
                    borderColor: releaseData.description.trim().length >= 10 ? '#52c41a' : releaseData.description.trim().length > 0 ? '#faad14' : undefined
                  }}
                  status={releaseData.description.trim().length >= 10 ? '' : releaseData.description.trim().length > 0 ? 'warning' : ''}
                />
              </div>
            </>
          )}
        </Space>
      </Modal>

      <Modal
        title="Update CRM"
        open={crmUpdateModalOpen}
        onOk={handleUpdateCrm}
        onCancel={handleCloseCrmUpdateModal}
        okText="Push to CRM"
        okButtonProps={{
          disabled: !crmUpdateData.cc_email.trim() || !crmUpdateData.sprint_number.trim(),
          loading: crmUpdating,
        }}
        cancelButtonProps={{ disabled: crmUpdating }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            message="This updates the linked CRM lead."
            description="CC emails and sprint number will be pushed to CRM, and the appointment will be set automatically to tomorrow at 00:00 IST."
            type="info"
            showIcon
          />

          <div>
            <Typography.Text strong>CC Email(s)</Typography.Text>
            <Input.TextArea
              rows={4}
              value={crmUpdateData.cc_email}
              onChange={(e) =>
                setCrmUpdateData({
                  ...crmUpdateData,
                  cc_email: e.target.value,
                })
              }
              placeholder={'first@example.com\nsecond@example.com'}
              disabled={crmUpdating}
              style={{ marginTop: 8 }}
            />
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              Paste one or more email addresses separated by commas, spaces, or new lines. These will be saved to the CRM cc_email field.
            </Typography.Text>
          </div>

          <div>
            <Typography.Text strong>Sprint Number</Typography.Text>
            <Input
              value={crmUpdateData.sprint_number}
              onChange={(e) =>
                setCrmUpdateData({
                  ...crmUpdateData,
                  sprint_number: e.target.value,
                })
              }
              placeholder="2026-Apr-company-name"
              disabled={crmUpdating}
              style={{ marginTop: 8 }}
            />
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              Autofilled as YYYY-MMM-company-name using the lead name. Edit it if the CRM sprint label should be different.
            </Typography.Text>
          </div>
        </Space>
      </Modal>

      {/* Create CRM Profile Modal */}
      <Modal
        title="Create CRM Profile"
        open={crmModalOpen}
        onOk={handleCreateCrmProfile}
        onCancel={() => { setCrmModalOpen(false); setCrmEmail('') }}
        okText="Create"
        okButtonProps={{ disabled: !crmEmail.trim(), loading: crmCreating }}
        cancelButtonProps={{ disabled: crmCreating }}
      >
        <Typography.Paragraph>
          Enter the email address for <strong>{currentLead?.company_name}</strong> to create a CRM profile.
        </Typography.Paragraph>
        <Input
          placeholder="company@example.com"
          value={crmEmail}
          onChange={(e) => setCrmEmail(e.target.value)}
          onPressEnter={handleCreateCrmProfile}
          type="email"
          disabled={crmCreating}
        />
      </Modal>
    </div>
  )
}

export default LeadProcessing
